import type { ColumnDef } from '@tanstack/react-table';
import type { LocaleLocaleAdminReadModel } from '@r/api/generated/client.schemas.ts';
import type { DataTableFilterField } from '@r/types/index.ts';
import { useTranslation } from 'react-i18next';
import DataTableRowActions from '@r/components/data-table/advanced/data-table-row-actions.tsx';

type Locale = LocaleLocaleAdminReadModel;

export const useFilterFields = (): DataTableFilterField<Locale>[] => {
  const { t } = useTranslation();
  return [
    {
      label: t('label.search'),
      value: 'search'
    },
    {
      label: t('label.translation.type'),
      value: 'isCustom',
      options: [
        { label: t('label.translation.custom'), value: 'true' },
        { label: t('label.translation.system'), value: 'false' }
      ]
    }
  ];
};

export const useTranslationColumns = (
  isUserAdmin: boolean,
  onEditCustom: (value: Locale) => void,
  onCreateCustom: (value: Locale) => void,
  onRollbackCustom: (value: Locale) => void
): ColumnDef<Locale>[] => {
  const { t } = useTranslation();

  const columns: ColumnDef<Locale>[] = [
    {
      accessorKey: 'label' as keyof Locale,
      id: 'label',
      header: t('label.translation.label')
    },
    {
      id: 'systemTranslation',
      header: t('label.translation.systemtranslation'),
      cell: ({ row }) => {
        const locale = row.original;

        const systemContent = locale.isCustom ? locale.defaultContent : locale.content;
        return <div title={systemContent ?? '-'}>{systemContent || '-'}</div>;
      }
    },
    {
      id: 'customTranslation',
      header: t('label.translation.customtranslation'),
      cell: ({ row }) => {
        const locale = row.original;
        const customContent = locale.isCustom ? locale.content : '';
        return <div title={customContent}>{customContent || '-'}</div>;
      }
    },
    {
      accessorKey: 'isCustom' as keyof Locale,
      id: 'isCustom',
      header: 'isCustom Filter (Hidden)',
      enableHiding: false,
      cell: () => null
    },
    {
      id: 'search',
      header: 'Search Filter (Hidden)',
      enableHiding: true,
      cell: () => null
    }
  ];

  const finalColumns = isUserAdmin
    ? columns
    : columns.filter((col) => col.id !== 'customTranslation');

  finalColumns.push({
    id: 'actions',
    cell: ({ row }) => (
      <DataTableRowActions
        row={row}
        isUserAdmin={isUserAdmin}
        onEditCustom={onEditCustom}
        onCreateCustom={onCreateCustom}
        onRollbackCustom={onRollbackCustom}
      />
    )
  });

  return finalColumns;
};
